import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  MessageCircle, Send, ArrowLeft, User, Search, 
  Loader2, Plus, X, Crown, Star, Trash2, MoreVertical, Smile, Image, Mic
} from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import GifPicker from '../components/GifPicker';
import VoiceRecorder from '../components/VoiceRecorder';
import FileAttachment from '../components/FileAttachment';
import MediaRenderer from '../components/MediaRenderer';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessagesPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [eligibleRecipients, setEligibleRecipients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [showConversationMenu, setShowConversationMenu] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [otherTyping, setOtherTyping] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const messagesEndRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const gifPickerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (conversationId) {
      fetchConversationMessages(conversationId);
    } else {
      setSelectedConversation(null);
      setMessages([]);
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Close emoji/gif picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
      if (gifPickerRef.current && !gifPickerRef.current.contains(event.target)) {
        setShowGifPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Poll for typing status when in a conversation
  useEffect(() => {
    if (!conversationId) {
      setOtherTyping(false);
      return;
    }
    const pollTyping = async () => {
      try {
        const res = await axios.get(
          `${API_URL}/messages/conversations/${conversationId}/typing`,
          { headers: getAuthHeader() }
        );
        setOtherTyping(res.data.is_typing);
        setTypingUser(res.data.typing_user);
      } catch {}
    };
    pollTyping();
    const interval = setInterval(pollTyping, 2000);
    return () => clearInterval(interval);
  }, [conversationId]);

  const sendTypingStatus = () => {
    if (!conversationId) return;
    const now = Date.now();
    // Throttle: only send once every 2 seconds
    if (now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;
    axios.post(`${API_URL}/messages/typing`, 
      { conversation_id: conversationId },
      { headers: getAuthHeader() }
    ).catch(() => {});
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const onEmojiClick = (emojiData) => {
    setNewMessage(prev => prev + emojiData.emoji);
  };

  const onGifSelect = async (gifData) => {
    // Send the GIF as a message
    if (!selectedConversation) return;
    
    setSending(true);
    try {
      const otherUserId = selectedConversation.participants.find(p => p !== user.id);
      const response = await axios.post(`${API_URL}/messages/send`, {
        recipient_id: otherUserId,
        content: `[GIF](${gifData.url})`
      }, {
        headers: getAuthHeader()
      });
      
      setMessages([...messages, response.data.message]);
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send GIF');
    } finally {
      setSending(false);
    }
  };

  const handleSendVoiceMessage = async (audioBlob, duration) => {
    if (!selectedConversation) return;
    // Upload the audio blob first
    const formData = new FormData();
    const ext = audioBlob.type.includes('webm') ? '.webm' : '.m4a';
    formData.append('file', audioBlob, `voice_message${ext}`);
    
    const uploadRes = await axios.post(`${API_URL}/media/upload`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
    
    const otherUserId = selectedConversation.participants.find(p => p !== user.id);
    const response = await axios.post(`${API_URL}/messages/send`, {
      recipient_id: otherUserId,
      content: 'Voice message',
      message_type: 'voice',
      file_url: uploadRes.data.file_url,
      file_name: uploadRes.data.file_name,
      file_size: uploadRes.data.file_size,
      duration
    }, { headers: getAuthHeader() });
    
    setMessages(prev => [...prev, response.data.message]);
    setShowVoiceRecorder(false);
    fetchConversations();
  };

  const handleFileUploaded = (fileData) => {
    setPendingFile(fileData);
  };

  const handleSendFileMessage = async () => {
    if (!pendingFile || !selectedConversation) return;
    setSending(true);
    try {
      const otherUserId = selectedConversation.participants.find(p => p !== user.id);
      
      // Map file_type to message_type
      let msgType = pendingFile.file_type;
      if (msgType === 'document') msgType = 'file';
      
      const response = await axios.post(`${API_URL}/messages/send`, {
        recipient_id: otherUserId,
        content: pendingFile.file_name || 'File',
        message_type: msgType,
        file_url: pendingFile.file_url,
        file_name: pendingFile.file_name,
        file_size: pendingFile.file_size
      }, { headers: getAuthHeader() });
      
      setMessages(prev => [...prev, response.data.message]);
      setPendingFile(null);
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send file');
    } finally {
      setSending(false);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/messages/conversations`, {
        headers: getAuthHeader()
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversationMessages = async (convId) => {
    try {
      const response = await axios.get(`${API_URL}/messages/conversations/${convId}`, {
        headers: getAuthHeader()
      });
      setSelectedConversation(response.data.conversation);
      setMessages(response.data.messages);
      // Refresh conversations to update unread counts
      fetchConversations();
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load conversation');
    }
  };

  const fetchEligibleRecipients = async () => {
    setLoadingRecipients(true);
    try {
      const response = await axios.get(`${API_URL}/messages/eligible-recipients`, {
        headers: getAuthHeader()
      });
      setEligibleRecipients(response.data);
    } catch (error) {
      console.error('Error fetching recipients:', error);
      toast.error('Failed to load recipients');
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleDeleteConversation = async (convId, e) => {
    e?.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this conversation? This cannot be undone.')) {
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/messages/conversations/${convId}`, {
        headers: getAuthHeader()
      });
      
      setConversations(conversations.filter(c => c.id !== convId));
      if (selectedConversation?.id === convId) {
        setSelectedConversation(null);
        setMessages([]);
        navigate('/dashboard/messages');
      }
      setShowConversationMenu(null);
      toast.success('Conversation deleted');
    } catch (error) {
      toast.error('Failed to delete conversation');
    } finally {
      setDeleting(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const otherUserId = selectedConversation.participants.find(p => p !== user.id);
      const response = await axios.post(`${API_URL}/messages/send`, {
        recipient_id: otherUserId,
        content: newMessage.trim()
      }, {
        headers: getAuthHeader()
      });
      
      setMessages([...messages, response.data.message]);
      setNewMessage('');
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleStartNewConversation = async (recipient) => {
    setSending(true);
    try {
      // Check if conversation already exists
      const existingConv = conversations.find(c => 
        c.participants.includes(recipient.id)
      );
      
      if (existingConv) {
        navigate(`/dashboard/messages/${existingConv.id}`);
        setShowNewMessage(false);
        return;
      }
      
      // Start with a greeting message
      const response = await axios.post(`${API_URL}/messages/send`, {
        recipient_id: recipient.id,
        content: `Hi ${recipient.name?.split(' ')[0] || 'there'}!`
      }, {
        headers: getAuthHeader()
      });
      
      await fetchConversations();
      navigate(`/dashboard/messages/${response.data.conversation_id}`);
      setShowNewMessage(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start conversation');
    } finally {
      setSending(false);
    }
  };

  const getOtherParticipantName = (conversation) => {
    if (!conversation || !user) return 'Unknown';
    const otherId = conversation.participants.find(p => p !== user.id);
    return conversation.participant_names?.[otherId] || 'Unknown';
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const filteredRecipients = eligibleRecipients.filter(r => 
    r.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTierBadge = (tier) => {
    if (tier === 'vip') return <Crown className="w-4 h-4 text-purple-500" />;
    if (tier === 'founding') return <Star className="w-4 h-4 text-amber-500" />;
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="messages-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Messages</h1>
          <p className="text-stone-600">Private conversations with community members</p>
        </div>
        <Button
          onClick={() => {
            setShowNewMessage(true);
            fetchEligibleRecipients();
          }}
          className="btn-primary"
          data-testid="new-message-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Message
        </Button>
      </div>

      <div className="card-cosmic overflow-hidden" style={{ height: 'calc(100vh - 250px)', minHeight: '500px' }}>
        <div className="flex h-full">
          {/* Conversations List */}
          <div className={`w-full md:w-1/3 border-r border-stone-200 flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
            <div className="p-4 border-b border-stone-200">
              <h2 className="font-semibold text-stone-900">Conversations</h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-8 text-center text-stone-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No conversations yet</p>
                  <p className="text-sm mt-2">Start a new message to connect with community members</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.id}
                    className={`relative w-full p-4 text-left hover:bg-stone-50 transition-colors border-b border-stone-100 ${
                      selectedConversation?.id === conv.id ? 'bg-purple-50' : ''
                    }`}
                    data-testid={`conversation-${conv.id}`}
                  >
                    <button
                      onClick={() => navigate(`/dashboard/messages/${conv.id}`)}
                      className="w-full text-left"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold flex-shrink-0">
                          {getOtherParticipantName(conv).charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0 pr-8">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-stone-900 truncate">
                              {getOtherParticipantName(conv)}
                            </span>
                            {conv.last_message_at && (
                              <span className="text-xs text-stone-500">
                                {formatTime(conv.last_message_at)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-stone-500 truncate mt-1">
                            {conv.last_message?.includes('[GIF]') 
                              ? 'Sent a GIF' 
                              : conv.last_message?.startsWith('Voice message') || conv.last_message?.startsWith('\u{1F3A4}')
                              ? 'Voice message'
                              : (conv.last_message || 'No messages yet')}
                          </p>
                          {conv.unread_count > 0 && (
                            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-primary rounded-full mt-1">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                    
                    {/* Conversation Menu */}
                    <div className="absolute right-2 top-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowConversationMenu(showConversationMenu === conv.id ? null : conv.id);
                        }}
                        className="p-1 hover:bg-stone-200 rounded-full transition-colors"
                      >
                        <MoreVertical className="w-4 h-4 text-stone-400" />
                      </button>
                      
                      {showConversationMenu === conv.id && (
                        <div className="absolute right-0 top-8 bg-white border border-stone-200 rounded-lg shadow-lg z-10 py-1 min-w-[140px]">
                          <button
                            onClick={(e) => handleDeleteConversation(conv.id, e)}
                            disabled={deleting}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Messages Area */}
          <div className={`flex-1 flex flex-col ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
            {selectedConversation ? (
              <>
                {/* Conversation Header */}
                <div className="p-4 border-b border-stone-200 flex items-center gap-3">
                  <button
                    onClick={() => navigate('/dashboard/messages')}
                    className="md:hidden p-2 hover:bg-stone-100 rounded-lg"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold">
                    {getOtherParticipantName(selectedConversation).charAt(0)}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-stone-900">
                      {getOtherParticipantName(selectedConversation)}
                    </h3>
                  </div>
                  <button
                    onClick={(e) => handleDeleteConversation(selectedConversation.id, e)}
                    disabled={deleting}
                    className="p-2 hover:bg-red-50 rounded-lg text-stone-400 hover:text-red-500 transition-colors"
                    title="Delete conversation"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((msg) => {
                    // Check if message is a GIF
                    const gifMatch = msg.content.match(/\[GIF\]\((.*?)\)/);
                    const isGif = gifMatch !== null;
                    const gifUrl = gifMatch ? gifMatch[1] : null;
                    const isOwn = msg.sender_id === user.id;
                    const hasMedia = msg.message_type && msg.message_type !== 'text' && msg.file_url;
                    
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-2xl ${isGif || hasMedia ? 'p-2' : 'px-4 py-2'} ${
                            isOwn
                              ? 'bg-primary text-white rounded-br-md'
                              : 'bg-stone-100 text-stone-900 rounded-bl-md'
                          }`}
                        >
                          {hasMedia ? (
                            <MediaRenderer
                              messageType={msg.message_type}
                              fileUrl={msg.file_url}
                              fileName={msg.file_name}
                              fileSize={msg.file_size}
                              duration={msg.duration}
                              isOwn={isOwn}
                            />
                          ) : isGif ? (
                            <img 
                              src={gifUrl} 
                              alt="GIF" 
                              className="rounded-xl max-w-full"
                              style={{ maxHeight: '200px' }}
                            />
                          ) : (
                            <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                          )}
                          <p className={`text-xs mt-1 ${isGif || hasMedia ? 'px-1 pb-0.5' : ''} ${
                            isOwn ? 'text-white/70' : 'text-stone-500'
                          }`}>
                            {formatTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  {/* Typing indicator */}
                  {otherTyping && (
                    <div className="flex justify-start" data-testid="typing-indicator">
                      <div className="bg-stone-100 rounded-2xl rounded-bl-md px-4 py-2">
                        <div className="flex items-center gap-1.5">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                          {typingUser && (
                            <span className="text-xs text-stone-500 ml-1">{typingUser.split(' ')[0]} is typing</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="p-4 border-t border-stone-200">
                  {/* Pending file preview */}
                  {pendingFile && (
                    <div className="flex items-center gap-2 mb-2 px-2">
                      <div className="flex-1 flex items-center gap-2 bg-purple-50 rounded-lg px-3 py-2">
                        <span className="text-sm text-purple-700 truncate">{pendingFile.file_name}</span>
                        <button
                          type="button"
                          onClick={() => setPendingFile(null)}
                          className="p-0.5 hover:bg-purple-100 rounded-full text-purple-400"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <Button
                        onClick={handleSendFileMessage}
                        disabled={sending}
                        className="btn-primary rounded-full px-4"
                        data-testid="send-file-btn"
                      >
                        {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                      </Button>
                    </div>
                  )}
                  
                  {showVoiceRecorder ? (
                    <div className="flex items-center justify-center py-2">
                      <VoiceRecorder
                        onSend={handleSendVoiceMessage}
                        onCancel={() => setShowVoiceRecorder(false)}
                        disabled={sending}
                      />
                    </div>
                  ) : (
                    <form onSubmit={handleSendMessage} className="flex gap-2 items-center relative">
                      <div className="relative" ref={emojiPickerRef}>
                        <button
                          type="button"
                          onClick={() => {
                            setShowEmojiPicker(!showEmojiPicker);
                            setShowGifPicker(false);
                          }}
                          className="p-2 hover:bg-stone-100 rounded-full transition-colors text-stone-500 hover:text-stone-700"
                          title="Add emoji"
                        >
                          <Smile className="w-5 h-5" />
                        </button>
                        {showEmojiPicker && (
                          <div className="absolute bottom-12 left-0 z-50">
                            <EmojiPicker
                              onEmojiClick={onEmojiClick}
                              width={320}
                              height={400}
                              previewConfig={{ showPreview: false }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="relative" ref={gifPickerRef}>
                        <button
                          type="button"
                          onClick={() => {
                            setShowGifPicker(!showGifPicker);
                            setShowEmojiPicker(false);
                          }}
                          className="p-2 hover:bg-stone-100 rounded-full transition-colors text-stone-500 hover:text-stone-700"
                          title="Add GIF"
                        >
                          <Image className="w-5 h-5" />
                        </button>
                        {showGifPicker && (
                          <div className="absolute bottom-12 left-0 z-50">
                            <GifPicker
                              onGifSelect={onGifSelect}
                              onClose={() => setShowGifPicker(false)}
                            />
                          </div>
                        )}
                      </div>
                      <FileAttachment
                        onFileUploaded={handleFileUploaded}
                        getAuthHeader={getAuthHeader}
                        disabled={sending}
                      />
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => {
                          setNewMessage(e.target.value);
                          sendTypingStatus();
                        }}
                        placeholder="Type a message..."
                        className="flex-1 px-4 py-2 border border-stone-200 rounded-full focus:outline-none focus:ring-2 focus:ring-primary/50"
                        disabled={sending}
                        data-testid="message-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowVoiceRecorder(true)}
                        className="p-2 hover:bg-stone-100 rounded-full transition-colors text-stone-500 hover:text-stone-700"
                        title="Record voice message"
                        data-testid="voice-record-btn"
                      >
                        <Mic className="w-5 h-5" />
                      </button>
                      <Button
                        type="submit"
                        disabled={!newMessage.trim() || sending}
                        className="btn-primary rounded-full px-4"
                        data-testid="send-message-btn"
                      >
                        {sending ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Send className="w-5 h-5" />
                        )}
                      </Button>
                    </form>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-stone-500">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Select a conversation</p>
                  <p className="text-sm mt-2">or start a new message</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Message Modal */}
      {showNewMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-stone-200 flex items-center justify-between">
              <h3 className="font-semibold text-lg">New Message</h3>
              <button
                onClick={() => setShowNewMessage(false)}
                className="p-2 hover:bg-stone-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 border-b border-stone-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search members..."
                  className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  data-testid="search-recipients"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loadingRecipients ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : filteredRecipients.length === 0 ? (
                <div className="p-8 text-center text-stone-500">
                  <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No members found</p>
                  <p className="text-sm mt-2">You can message members in your same tier</p>
                </div>
              ) : (
                filteredRecipients.map((recipient) => (
                  <button
                    key={recipient.id}
                    onClick={() => handleStartNewConversation(recipient)}
                    disabled={sending}
                    className="w-full p-4 text-left hover:bg-stone-50 transition-colors border-b border-stone-100 flex items-center gap-3"
                    data-testid={`recipient-${recipient.id}`}
                  >
                    {recipient.avatar_url ? (
                      <img
                        src={recipient.avatar_url}
                        alt={recipient.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold">
                        {recipient.name?.charAt(0) || '?'}
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-stone-900">{recipient.name}</span>
                        {recipient.is_admin && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                            Coach
                          </span>
                        )}
                        {getTierBadge(recipient.membership_tier)}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
